type Download = {
    url: string;
    name: string;
    size: number;
};

type Callbacks = {
    onStart?: (file: Download) => void;
    onProgress?: (file: Download, progress: number) => void;
    onComplete?: (file: Download) => void;
    onError?: (file: Download, error: unknown) => void;
};

async function downloadFile(
    { url, name }: { url: string; name?: string | null },
    folderHandle: FileSystemDirectoryHandle | null,
    callbacks?: Callbacks
) {
    if (folderHandle) {
        await downloadViaFSAA(url, name, folderHandle, callbacks);
    } else {
        await downloadViaBlob(url, name, callbacks);
    }
}

function extractData(res: Response) {
    const contentDisposition = res.headers.get('Content-Disposition');
    const fileName =
        (contentDisposition
            ? contentDisposition.split('filename=')[1].replace(/"/g, '')
            : res.url.split('/').pop()) || '';

    const fileSize = Number(res.headers.get('Content-Length') || 0);

    return { fileName, fileSize };
}

// Function to handle downloading via File System Access API (FSAA)
async function downloadViaFSAA(
    url: string,
    name: string | undefined | null,
    folderHandle: FileSystemDirectoryHandle,
    callbacks: Callbacks = {}
) {
    name = name || '';
    let fileSize = 0;
    try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`Failed to fetch ${url}: ${res.status}`);

        const data = extractData(res);

        name = name || data.fileName;
        fileSize = data.fileSize;

        const fileHandle = await folderHandle.getFileHandle(name, { create: true });
        const writable = await fileHandle.createWritable();
        const reader = res.body?.getReader();

        if (!reader) throw new Error('ReadableStream not supported');

        const writer = writable.getWriter();
        let done = false;
        callbacks.onStart?.({ url, name, size: fileSize });

        let size = 0;
        while (!done) {
            const { done: chunkDone, value } = await reader.read();
            done = chunkDone;
            if (value) {
                size += value.byteLength;
                await writer.write(value);
                callbacks.onProgress?.({ url, name, size: fileSize }, size / fileSize);
            }
        }
        callbacks.onComplete?.({ url, name, size: fileSize });

        await writer.close();
    } catch (error) {
        callbacks.onError?.({ url, name, size: fileSize }, error);
    }
}

async function downloadViaBlob(url: string, name?: string | null, callbacks: Callbacks = {}) {
    let fileName = '';
    let fileSize = 0;
    try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`Failed to fetch ${url}: ${res.status}`);
        ({ fileName, fileSize } = extractData(res));

        name = (name || fileName)!;

        callbacks.onStart?.({ url, name, size: fileSize });

        // Progress tracking
        const reader = res.body?.getReader();
        if (!reader) throw new Error('ReadableStream not supported');

        const chunks: Uint8Array[] = [];
        let received = 0;
        let done = false;

        while (!done) {
            const { done: chunkDone, value } = await reader.read();
            done = chunkDone;
            if (value) {
                chunks.push(value);
                received += value.byteLength;
                if (fileSize > 0) {
                    callbacks.onProgress?.({ url, name, size: fileSize }, received / fileSize);
                }
            }
        }

        const blob = new Blob(chunks);
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = name;
        document.body.appendChild(a);

        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(a.href);
        callbacks.onComplete?.({ url, name, size: fileSize });
    } catch (error) {
        callbacks.onError?.({ url, name: name || fileName, size: fileSize }, error);
    }
}

async function ensureNotificationPermission() {
    if (Notification.permission === 'granted') return true;
    if (Notification.permission === 'denied') return false;
    const permission = await Notification.requestPermission();
    return permission === 'granted';
}

export async function downloadMultipleFiles(
    urls: { url: string; name?: string | null }[],
    callbacks?: Callbacks
) {
    let folderHandle: FileSystemDirectoryHandle | null = null;
    let notificationsAuthorized = false;

    // Ask for folder once at the start of the process
    if ('showDirectoryPicker' in window) {
        const showDirectoryPicker = window.showDirectoryPicker as () => FileSystemDirectoryHandle;
        notificationsAuthorized = await ensureNotificationPermission();

        folderHandle = await showDirectoryPicker();
    }

    // Start downloading the files
    for (let url of urls) {
        await downloadFile(url, folderHandle, callbacks);
    }

    if (notificationsAuthorized) {
        new Notification('Download complete', {
            body: 'All files have been downloaded successfully.'
        });
    }
}
